import { AfterViewInit, Component, ElementRef, HostListener, Inject, OnDestroy, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import Konva from 'konva';
import { MEntryService } from '../../services/m-entry-service/m-entry.service';
import { NotificationService } from '../../services/notification-service/notification.service';

export interface PhotoAnnotateDialogData {
  entryId: string;
  /** The original to draw on. Annotating a copy is not allowed — its marks would stack. */
  fileId: string;
  /** True when the photo already has an annotated copy that saving will replace. */
  replacesCopy?: boolean;
}

export type AnnotateTool = 'select' | 'arrow' | 'ellipse' | 'text';

export const ANNOTATE_COLORS = ['#e53935', '#fdd835', '#ffffff', '#212121'];

/**
 * DOCU-1143: marks a photo with arrows, ellipses and text and stores the result as an annotated
 * copy (DOCU-1142). The original stays untouched and is hidden in the export; the copy takes its
 * place. Shapes are drawn in image pixels on a scaled layer, so the export has the original's
 * resolution whatever the size of the screen. Closes with `true` after a successful save.
 */
@Component({
  selector: 'app-photo-annotate-dialog',
  templateUrl: './photo-annotate-dialog.component.html',
  styleUrl: './photo-annotate-dialog.component.scss'
})
export class PhotoAnnotateDialogComponent implements AfterViewInit, OnDestroy {
  @ViewChild('stageHost') stageHost!: ElementRef<HTMLDivElement>;

  readonly colors = ANNOTATE_COLORS;
  tool: AnnotateTool = 'arrow';
  color = ANNOTATE_COLORS[0];
  text = '';
  loading = true;
  saving = false;
  loadFailed = false;
  /** Shapes in drawing order — undo removes the last one. */
  shapes: Konva.Shape[] = [];
  selected: Konva.Shape | null = null;

  private stage?: Konva.Stage;
  private drawLayer?: Konva.Layer;
  private transformer?: Konva.Transformer;
  private objectUrl?: string;
  /** Screen pixels per image pixel. */
  private scale = 1;
  private imageSize = { width: 0, height: 0 };
  private strokeWidth = 4;
  private drawing?: { shape: Konva.Arrow | Konva.Ellipse; startX: number; startY: number };

  constructor(
    public dialogRef: MatDialogRef<PhotoAnnotateDialogComponent, boolean>,
    @Inject(MAT_DIALOG_DATA) public data: PhotoAnnotateDialogData,
    private entryService: MEntryService,
    private notificationService: NotificationService
  ) {}

  ngAfterViewInit(): void {
    this.entryService.getPhotoContent(this.data.entryId, this.data.fileId).subscribe({
      next: (blob) => {
        this.objectUrl = URL.createObjectURL(blob);
        const image = new Image();
        image.onload = () => this.buildStage(image);
        image.onerror = () => this.failLoading();
        image.src = this.objectUrl;
      },
      error: () => this.failLoading()
    });
  }

  ngOnDestroy(): void {
    this.stage?.destroy();
    if (this.objectUrl) URL.revokeObjectURL(this.objectUrl);
  }

  get showsTextField(): boolean {
    return this.tool === 'text' || this.selected instanceof Konva.Text;
  }

  setTool(tool: AnnotateTool): void {
    this.tool = tool;
    if (tool !== 'select') this.select(null);
    for (const shape of this.shapes) shape.draggable(tool === 'select');
  }

  setColor(color: string): void {
    this.color = color;
    if (!this.selected) return;
    if (this.selected instanceof Konva.Text) {
      this.selected.fill(color);
    } else {
      this.selected.stroke(color);
      if (this.selected instanceof Konva.Arrow) this.selected.fill(color);
    }
  }

  onTextChange(value: string): void {
    this.text = value;
    if (this.selected instanceof Konva.Text) this.selected.text(value || ' ');
  }

  undo(): void {
    const last = this.shapes.pop();
    if (!last) return;
    if (last === this.selected) this.select(null);
    last.destroy();
  }

  @HostListener('document:keydown.delete')
  deleteSelected(): void {
    // Typing in the text field must not delete the selected shape.
    if (!this.selected || document.activeElement instanceof HTMLInputElement) return;
    const shape = this.selected;
    this.select(null);
    this.shapes = this.shapes.filter((s) => s !== shape);
    shape.destroy();
  }

  save(): void {
    if (!this.stage || this.saving || this.shapes.length === 0) return;
    this.saving = true;
    this.select(null);

    this.exportFullSize()
      .then((blob) => {
        if (!(blob instanceof Blob)) throw new Error('canvas export failed');
        this.entryService.postAnnotatedCopy(this.data.entryId, this.data.fileId, blob).subscribe({
          next: () => {
            this.saving = false;
            this.notificationService.showSuccess('AUDIT.ANNOTATE.SAVED');
            this.dialogRef.close(true);
          },
          error: () => this.failSaving()
        });
      })
      .catch(() => this.failSaving());
  }

  /**
   * Renders the stage at the original's pixel size. A pixelRatio of 1/scale would round the
   * scaled stage size and miss the original by a few pixels. Konva draws the canvas synchronously
   * inside toBlob, so the screen size can be restored right after the call.
   */
  private exportFullSize(): Promise<Blob> {
    const stage = this.stage!;
    const shown = stage.size();
    stage.size(this.imageSize);
    stage.getLayers().forEach((layer) => layer.scale({ x: 1, y: 1 }));
    try {
      return stage.toBlob({ pixelRatio: 1, mimeType: 'image/jpeg', quality: 0.92 }) as Promise<Blob>;
    } finally {
      stage.size(shown);
      stage.getLayers().forEach((layer) => layer.scale({ x: this.scale, y: this.scale }));
    }
  }

  private buildStage(image: HTMLImageElement): void {
    const host = this.stageHost.nativeElement;
    const maxWidth = Math.max(host.clientWidth, 200);
    const maxHeight = Math.max(window.innerHeight * 0.65, 200);
    const width = image.naturalWidth;
    const height = image.naturalHeight;
    this.imageSize = { width, height };
    this.scale = Math.min(maxWidth / width, maxHeight / height, 1);
    // Line width and font size in image pixels, relative to the photo — a 12 MP photo needs
    // thicker lines than a screenshot to look the same in the report.
    this.strokeWidth = Math.max(3, Math.round(Math.max(width, height) / 250));

    this.stage = new Konva.Stage({
      container: host,
      width: Math.round(width * this.scale),
      height: Math.round(height * this.scale)
    });
    const imageLayer = new Konva.Layer({ listening: false, scaleX: this.scale, scaleY: this.scale });
    imageLayer.add(new Konva.Image({ image, width, height }));
    this.drawLayer = new Konva.Layer({ scaleX: this.scale, scaleY: this.scale });
    this.transformer = new Konva.Transformer({ rotateEnabled: false, keepRatio: false });
    this.drawLayer.add(this.transformer);
    this.stage.add(imageLayer, this.drawLayer);

    this.stage.on('pointerdown', (e) => this.onPointerDown(e));
    this.stage.on('pointermove', () => this.onPointerMove());
    this.stage.on('pointerup pointerleave', () => this.finishDrawing());

    this.loading = false;
  }

  private onPointerDown(e: Konva.KonvaEventObject<PointerEvent>): void {
    const pos = this.drawLayer!.getRelativePointerPosition();
    if (!pos) return;

    if (this.tool === 'select') {
      this.select(e.target instanceof Konva.Shape && this.shapes.includes(e.target) ? e.target : null);
      return;
    }

    if (this.tool === 'text') {
      const text = new Konva.Text({
        x: pos.x,
        y: pos.y,
        text: this.text || 'Text',
        fontSize: this.strokeWidth * 8,
        fontStyle: 'bold',
        fill: this.color,
        stroke: this.color === '#212121' ? '#ffffff' : '#212121',
        strokeWidth: Math.max(1, this.strokeWidth / 4),
        fillAfterStrokeEnabled: true
      });
      this.add(text);
      this.setTool('select');
      this.select(text);
      return;
    }

    const shape = this.tool === 'arrow'
      ? new Konva.Arrow({
          points: [pos.x, pos.y, pos.x, pos.y],
          stroke: this.color,
          fill: this.color,
          strokeWidth: this.strokeWidth,
          pointerLength: this.strokeWidth * 4,
          pointerWidth: this.strokeWidth * 4,
          lineCap: 'round'
        })
      : new Konva.Ellipse({
          x: pos.x,
          y: pos.y,
          radiusX: 0,
          radiusY: 0,
          stroke: this.color,
          strokeWidth: this.strokeWidth
        });
    this.drawLayer!.add(shape);
    this.drawing = { shape, startX: pos.x, startY: pos.y };
  }

  private onPointerMove(): void {
    if (!this.drawing) return;
    const pos = this.drawLayer!.getRelativePointerPosition();
    if (!pos) return;
    const { shape, startX, startY } = this.drawing;
    if (shape instanceof Konva.Arrow) {
      shape.points([startX, startY, pos.x, pos.y]);
    } else {
      shape.setAttrs({
        x: (startX + pos.x) / 2,
        y: (startY + pos.y) / 2,
        radiusX: Math.abs(pos.x - startX) / 2,
        radiusY: Math.abs(pos.y - startY) / 2
      });
    }
  }

  private finishDrawing(): void {
    if (!this.drawing) return;
    const { shape } = this.drawing;
    this.drawing = undefined;
    const box = shape.getClientRect({ skipStroke: true, relativeTo: this.drawLayer });
    // A click without dragging leaves nothing worth keeping.
    if (Math.max(box.width, box.height) < this.strokeWidth * 3) {
      shape.destroy();
      return;
    }
    this.add(shape);
  }

  private add(shape: Konva.Shape): void {
    this.drawLayer!.add(shape);
    this.transformer!.moveToTop();
    this.shapes.push(shape);
  }

  private select(shape: Konva.Shape | null): void {
    this.selected = shape;
    this.transformer?.nodes(shape ? [shape] : []);
    if (shape instanceof Konva.Text) this.text = shape.text();
  }

  private failLoading(): void {
    this.loading = false;
    this.loadFailed = true;
    this.notificationService.showError('AUDIT.ANNOTATE.LOAD_ERROR');
  }

  private failSaving(): void {
    this.saving = false;
    this.notificationService.showError('AUDIT.ANNOTATE.SAVE_ERROR');
  }
}
